import moment from 'moment';
import {
  deserializeYesplanAPIDateAndTime,
  getDateAndTimeWithHoursAndMinutesSet,
  isDateAndTimeAfter,
  isDateAndTimeBefore,
  isDateAndTimeSameOrAfter,
  isDateAndTimeSameOrBefore,
  serializeYesplanAPIDateAndTime
} from '../libs/dateTimeUtilities';
import {
  ActualStateCancelledAfterFinalStateWarning,
  ActualStateCancelledWarning,
  ActualStateSickLeaveAfterFinalStateWarning,
  ActualStateSickLeaveWarning,
  ActualTimesBeforeSubmittingWarning,
  ActualTimesDifferAfterSubmittingWarning,
  BookingAddedAfterSubmittingWarning,
  BookingWithActualTimesAddedAfterSubmittingWarning,
  EndTimeBeforeStartTimeWarning,
  InvalidDurationOfTimesheetItemWarning,
  NoActualTimesInFinalStateWarning,
  StartTimeAfterEndTimeWarning
} from '../libs/timesheetWarnings';

export default class YPTimesheetEntryObject {
  constructor(entryJSON, bookingDetails, eventDetails) {
    this.entryJSON = entryJSON;
    this.bookingDetails = bookingDetails;
    this.eventDetails = eventDetails;
    this.showOnlyStartOrEnd = false;
    this.timesheetstart =
      this.entryJSON.timesheetstart !== null
        ? deserializeYesplanAPIDateAndTime(this.entryJSON.timesheetstart)
        : null;
    this.timesheetend =
      this.entryJSON.timesheetend !== null
        ? deserializeYesplanAPIDateAndTime(this.entryJSON.timesheetend)
        : null;
    this.warnings = [];
    this.timesheetStartEdit = null;
    this.timesheetEndEdit = null;
  }

  getId() {
    return this.entryJSON.id;
  }

  getIdWithOnlyStartOrEnd() {
    if (!this.showOnlyStartOrEnd) return this.getId();
    return `${this.getId()}-${this.showOnlyStartOrEnd}`;
  }

  getIdForDuplicateShiftEntry() {
    return `${this.getId()}-${this.showOnlyStartOrEnd === 'start' ? 'end' : 'start'}`;
  }

  getType() {
    return this.entryJSON.scheduletype;
  }

  setBookingDetails(bookingDetails) {
    this.bookingDetails = bookingDetails;
  }

  setEventDetails(eventDetails) {
    this.eventDetails = eventDetails;
  }

  getPlannedStart() {
    return deserializeYesplanAPIDateAndTime(this.entryJSON.start);
  }

  getPlannedEnd() {
    return deserializeYesplanAPIDateAndTime(this.entryJSON.end);
  }

  getTimesheetStart(plannedTimeAsFallback = true) {
    const plannedTime = deserializeYesplanAPIDateAndTime(this.entryJSON.start);
    if (this.timesheetstart === null && plannedTimeAsFallback) return plannedTime;
    return this.timesheetstart;
  }

  getTimesheetEnd(plannedTimeAsFallback = true) {
    const plannedTime = deserializeYesplanAPIDateAndTime(this.entryJSON.end);
    if (this.timesheetend === null && plannedTimeAsFallback) return plannedTime;
    return this.timesheetend;
  }

  getActualStart() {
    return this.entryJSON.actualstart
      ? deserializeYesplanAPIDateAndTime(this.entryJSON.actualstart)
      : null;
  }

  getActualEnd() {
    return this.entryJSON.actualend
      ? deserializeYesplanAPIDateAndTime(this.entryJSON.actualend)
      : null;
  }

  hasActuals() {
    return this.getActualEnd() || this.getActualStart();
  }

  hasActualStatus() {
    return typeof this.entryJSON.actualstatus !== 'undefined';
  }

  hasActualStatusSickLeave() {
    if (!this.hasActualStatus()) return false;
    return this.entryJSON.actualstatus === 'ill';
  }

  hasActualStatusCancelled() {
    if (!this.hasActualStatus()) return false;
    return this.entryJSON.actualstatus === 'cancelled';
  }

  getActualStatus() {
    return this.entryJSON.actualstatus;
  }

  /* From the moment the timesheetshart or end has been altered, we see this entry as "changed".
     Otherwise the autosave would not be triggered when for example first decrementing the time with 15 minutes,
     and afterwards adding 15 minutes such that it is still the same */
  hasChanged() {
    return this.timesheetstart !== null || this.timesheetend !== null;
  }

  setTimesheetStart(newStartDateTime) {
    this.timesheetstart = newStartDateTime;
  }

  setTimesheetEnd(newEndDateTime) {
    this.timesheetend = newEndDateTime;
  }

  getTimesheetStartEdit() {
    return this.timesheetStartEdit || this.getTimesheetStart();
  }

  getTimesheetEndEdit() {
    return this.timesheetEndEdit || this.getTimesheetEnd();
  }

  setTimesheetStartEdit(newStartDateTime) {
    this.timesheetStartEdit = newStartDateTime;
  }

  setTimesheetEndEdit(newEndDateTime) {
    this.timesheetEndEdit = newEndDateTime;
  }

  resetTimesheetEditTimes() {
    this.timesheetStartEdit = null;
    this.timesheetEndEdit = null;
  }

  hasTimesheetEnd() {
    return this.timesheetend !== null;
  }

  hasTimesheetStart() {
    return this.timesheetstart !== null;
  }

  hasTimesheetTimes() {
    return this.hasTimesheetStart() && this.hasTimesheetEnd();
  }

  /**
   * Return whether a processed timesheet time has been removed afterwards.
   * Not useful when the timesheet has not yet been processed.
   */
  hasTimesheetEndTimeWithoutActualEndTime() {
    return this.hasTimesheetEnd() && !this.getActualEnd();
  }

  hasTimesheetStartTimeWithoutActualStartTime() {
    return this.hasTimesheetStart() && !this.getActualStart();
  }

  bookingTimeAddedAfterwards(hasTimesheetTime) {
    return !hasTimesheetTime;
  }

  timesheetEndDiffersFromActualEndTime() {
    if (this.bookingTimeAddedAfterwards(this.hasTimesheetEnd())) {
      return true;
    }
    if (this.getActualEnd()) {
      return !this.getActualEnd().isSame(this.getTimesheetEnd());
    }
    return false;
  }

  timesheetStartDiffersFromActualStartTime() {
    if (this.bookingTimeAddedAfterwards(this.hasTimesheetStart())) {
      return true;
    }
    if (this.getActualStart()) {
      return !this.getActualStart().isSame(this.getTimesheetStart());
    }
    return false;
  }

  #isDeltaBetweenTimesValid(dateAndTime1, dateAndTime2) {
    const differenceInMinutes = dateAndTime1.diff(dateAndTime2, 'minute');
    return !(differenceInMinutes >= 12 * 60 || differenceInMinutes < -12 * 60);
  }

  #dateTimeInterpretedAround(dateAndTime, centerDateAndTime) {
    const timeOnCenterDate = getDateAndTimeWithHoursAndMinutesSet(
      centerDateAndTime,
      dateAndTime
    );
    const differenceInMinutes = timeOnCenterDate.diff(centerDateAndTime, 'minute');
    if (differenceInMinutes >= 12 * 60) return timeOnCenterDate.add(-1, 'day');
    if (differenceInMinutes < -12 * 60) return timeOnCenterDate.add(1, 'day');
    return timeOnCenterDate;
  }

  #getStartTimeInterpretedAroundPlannedStart() {
    const plannedStartDateTime = this.getPlannedStart();
    const timesheetStartDateTime = this.getTimesheetStartEdit().clone();
    return this.#dateTimeInterpretedAround(timesheetStartDateTime, plannedStartDateTime);
  }

  #getEndTimeInterpretedAroundPlannedEnd() {
    const plannedEnd = this.getPlannedEnd();
    const timesheetEndDateTime = this.getTimesheetEndEdit().clone();
    return this.#dateTimeInterpretedAround(timesheetEndDateTime, plannedEnd);
  }

  correctTimesheetStartOrEndTime() {
    const timesheetStart = this.#getStartTimeInterpretedAroundPlannedStart();
    const timesheetEnd = this.#getEndTimeInterpretedAroundPlannedEnd();
    const hasStartTimeChanged = this.timesheetStartEdit !== null;
    const hasEndTimeChanged = this.timesheetEndEdit !== null;

    /* Do not set the edit times as new timesheet times when they generate a warning,
     *  otherwise they would be used for intermediate autosaves.
     * However, we perform delta corrections on the edit times, to ensure they are in the [-12h, +12h[ interval */
    if (
      hasStartTimeChanged &&
      !this.canPerformTimeChangeOnTimesheetStartTime(timesheetStart, timesheetEnd)
    ) {
      this.addWarning(new StartTimeAfterEndTimeWarning());
      this.timesheetStartEdit = timesheetStart;
      return;
    }

    if (
      hasEndTimeChanged &&
      !this.canPerformTimeChangeOnTimesheetEndTime(timesheetEnd, timesheetStart)
    ) {
      this.addWarning(new EndTimeBeforeStartTimeWarning());
      this.timesheetEndEdit = timesheetEnd;
      return;
    }

    if (
      !this.canPerformTimeChangesWithoutExceedingTotalDuration(
        timesheetStart,
        timesheetEnd
      )
    ) {
      this.addWarning(new InvalidDurationOfTimesheetItemWarning());
      this.timesheetStartEdit = timesheetStart;
      this.timesheetEndEdit = timesheetEnd;
      return;
    }

    this.setTimesheetStart(timesheetStart);
    this.setTimesheetEnd(timesheetEnd);
    this.resetTimesheetEditTimes();
  }

  canPerformTimeChangeOnTimesheetStartTime(proposedDateAndTime, proposedEndDateAndTime) {
    const endTime = proposedEndDateAndTime || this.getTimesheetEndEdit();
    return (
      endTime.isAfter(proposedDateAndTime) &&
      this.#isDeltaBetweenTimesValid(proposedDateAndTime, this.getPlannedStart())
    );
  }

  canPerformTimeChangeOnTimesheetEndTime(proposedDateAndTime, proposedStartDateAndTime) {
    const startTime = proposedStartDateAndTime || this.getTimesheetStartEdit();
    return (
      startTime.isBefore(proposedDateAndTime) &&
      this.#isDeltaBetweenTimesValid(proposedDateAndTime, this.getPlannedEnd())
    );
  }

  canPerformTimeChangeWithoutExceedingDeltaForStart(proposedDateAndTime) {
    return this.#isDeltaBetweenTimesValid(proposedDateAndTime, this.getPlannedStart());
  }

  canPerformTimeChangeWithoutExceedingDeltaForEnd(proposedDateAndTime) {
    return this.#isDeltaBetweenTimesValid(proposedDateAndTime, this.getPlannedEnd());
  }

  /* This method checks if the current combination of timesheet times would be accepted by the BO.
   *  Concretely, this means that there is no end time that is
   *  1. is nearer in time to the timesheet start
   *  2. still a valid time: is later than the current timesheet start time
   */
  canPerformTimeChangesWithoutExceedingTotalDuration(
    proposedTimesheetStartTime,
    proposedTimesheetEndTime
  ) {
    const timesheetEndInterpretedAroundPlannedStart = this.#dateTimeInterpretedAround(
      this.getTimesheetEndEdit(),
      this.getPlannedStart()
    );

    return (
      timesheetEndInterpretedAroundPlannedStart.isSame(
        proposedTimesheetEndTime,
        'minutes'
      ) ||
      !timesheetEndInterpretedAroundPlannedStart.isAfter(
        proposedTimesheetStartTime,
        'minutes'
      )
    );
  }

  getDeltaDurationForStart() {
    let timesheetTime = this.getTimesheetStart();
    /* When we have a warning on invalid times currently, we consider the
       current (invalid) input to calculate the delta with. Otherwise, the delta with the
       previous valid value would always be shown */
    if (this.hasInvalidTimesWarning()) timesheetTime = this.getTimesheetStartEdit();
    return moment.duration(timesheetTime.diff(this.getPlannedStart()));
  }

  getDeltaDurationForEnd() {
    let timesheetTime = this.getTimesheetEnd();
    /* When we have a warning on invalid times currently, we consider the
       current (invalid) input to calculate the delta with. Otherwise, the delta with the
       previous valid value would always be shown */
    if (this.hasInvalidTimesWarning()) timesheetTime = this.getTimesheetEndEdit();
    return moment.duration(timesheetTime.diff(this.getPlannedEnd()));
  }

  isValid() {
    return !this.warnings.some((warning) => warning.showAsInvalid());
  }

  isValidForStartTime() {
    return !this.warnings.some((warning) => warning.showAsInvalidOnStartTime());
  }

  isValidForEndTime() {
    return !this.warnings.some((warning) => warning.showAsInvalidOnEndTime());
  }

  getShowOnlyStartOrEnd() {
    return this.showOnlyStartOrEnd;
  }

  setShowOnlyStart() {
    this.showOnlyStartOrEnd = 'start';
  }

  setShowOnlyEnd() {
    this.showOnlyStartOrEnd = 'end';
  }

  shouldShowStart() {
    if (this.isShift()) return this.showOnlyStartOrEnd === 'start';
    return true;
  }

  shouldShowEnd() {
    if (this.isShift()) return this.showOnlyStartOrEnd === 'end';
    return true;
  }

  getTitle() {
    switch (this.getType()) {
      case 'resourcebooking':
        if (this.hasLinkedEventWithLocation()) return this.getLocationTitle();
        return this.getBookingName();
      case 'shift':
        if (this.showOnlyStartOrEnd === 'start') {
          return i18n('Start of Shift');
        }
        return i18n('End of Shift');
      case 'break':
        return i18n('Break');
      default:
        return '';
    }
  }

  getLocationTitle() {
    return this.linkedEventLocationTitle();
  }

  getBookingName() {
    if (this.linkedBookingEvent()) {
      return this.linkedBookingEvent()?.name;
    }
    return i18n('Undisclosed Booking');
  }

  getRoleTitle() {
    return this.linkedBookingRole();
  }

  isShift() {
    return this.getType() === 'shift';
  }

  isBreak() {
    return this.getType() === 'break';
  }

  isResourceBooking() {
    return this.getType() === 'resourcebooking';
  }

  overlapsWithEntry(entry) {
    const start1 = this.getTimesheetStart();
    const end1 = this.getTimesheetEnd();
    const start2 = entry.getTimesheetStart();
    const end2 = entry.getTimesheetEnd();
    return isDateAndTimeAfter(end1, start2) && isDateAndTimeAfter(end2, start1);
  }

  isEnclosedByEntryWithPlannedTimes(entry) {
    const start1 = this.getPlannedStart();
    const end1 = this.getPlannedEnd();
    const start2 = entry.getPlannedStart();
    const end2 = entry.getPlannedEnd();
    return isDateAndTimeAfter(end1, start2) && isDateAndTimeAfter(end2, start1);
  }

  isEntirelyEnclosedByEntryWithPlannedTimes(entry) {
    const start1 = this.getPlannedStart();
    const end1 = this.getPlannedEnd();
    const start2 = entry.getPlannedStart();
    const end2 = entry.getPlannedEnd();
    return (
      isDateAndTimeSameOrAfter(start1, start2) && isDateAndTimeSameOrBefore(end1, end2)
    );
  }

  exceedsEnclosingEntryWithTimesheetTimes(entry) {
    const start1 = this.getTimesheetStart();
    const end1 = this.getTimesheetEnd();
    const start2 = entry.getTimesheetStart();
    const end2 = entry.getTimesheetEnd();
    return isDateAndTimeBefore(start1, start2) || isDateAndTimeAfter(end1, end2);
  }

  addWarningsOnActualStartAndEndTime() {
    const hasWarningOnStart =
      this.hasTimesheetStart() && this.timesheetStartDiffersFromActualStartTime();
    const hasWarningOnEnd =
      this.hasTimesheetEnd() && this.timesheetEndDiffersFromActualEndTime();
    if (hasWarningOnStart || hasWarningOnEnd)
      this.addWarning(
        new ActualTimesDifferAfterSubmittingWarning(
          this,
          hasWarningOnStart,
          hasWarningOnEnd
        )
      );
  }

  addWarningsOnActualTimes(timesheetInFinalState) {
    if (!timesheetInFinalState && this.hasActuals())
      this.addWarning(new ActualTimesBeforeSubmittingWarning(this));
    else if (timesheetInFinalState) {
      if (!this.hasTimesheetTimes()) {
        this.addWarning(new BookingWithActualTimesAddedAfterSubmittingWarning(this));
      }
      this.addWarningsOnActualStartAndEndTime();
    }
  }

  hasTimesheetTimeWithoutActualTime() {
    return (
      this.hasTimesheetStartTimeWithoutActualStartTime() ||
      this.hasTimesheetEndTimeWithoutActualEndTime()
    );
  }

  addWarningsAboutNoActualTimes(timesheetInFinalState) {
    if (timesheetInFinalState) {
      if (this.hasTimesheetTimeWithoutActualTime()) {
        this.addWarning(new NoActualTimesInFinalStateWarning(this));
      }
    }
  }

  addWarningsOnActualStatus(timesheetInFinalState) {
    if (timesheetInFinalState) {
      if (this.hasActualStatusSickLeave()) {
        this.addWarning(new ActualStateSickLeaveAfterFinalStateWarning(this));
      }
      if (this.hasActualStatusCancelled()) {
        this.addWarning(new ActualStateCancelledAfterFinalStateWarning(this));
      }
    } else {
      if (this.hasActualStatusSickLeave()) {
        this.addWarning(new ActualStateSickLeaveWarning(this));
      }
      if (this.hasActualStatusCancelled()) {
        this.addWarning(new ActualStateCancelledWarning(this));
      }
    }
  }

  addWarningsAboutNoTimesheetTimesOrActualTimesForBooking(notEditable) {
    if (notEditable) {
      if (!this.hasTimesheetTimes()) {
        this.addWarning(new BookingAddedAfterSubmittingWarning(this));
      }
    }
  }

  getWarnings() {
    return this.warnings;
  }

  hasWarnings() {
    return this.warnings.length > 0;
  }

  hasChangedAfterApprovalWarnings() {
    return this.warnings.some((warning) => warning.isChangedAfterApprovalError());
  }

  hasBlockingWarnings() {
    return this.warnings.some((warning) => warning.isError());
  }

  hasInvalidTimesWarning() {
    return this.warnings.some((warning) => warning.isInvalidTimeWarning());
  }

  resetWarnings() {
    this.warnings = [];
  }

  addWarning(warning) {
    /* Exception: do not add a start time warning on the end of shift entry
     *   and vice versa */
    if (this.getShowOnlyStartOrEnd() === 'end' && !warning.showAsInvalidOnEndTime())
      return;
    if (
      this.getShowOnlyStartOrEnd() === 'start' &&
      !warning.showAsInvalidOnStartTime() &&
      !warning.consistentShiftStartWarning()
    )
      return;
    this.warnings.push(warning);
  }

  hasLinkedEventWithLocation() {
    return (
      this.eventDetails &&
      this.eventDetails.locations &&
      this.eventDetails.locations.length > 0
    );
  }

  linkedEventLocationTitle() {
    if (!this.hasLinkedEventWithLocation()) return false;
    return this.eventDetails.locations[0].name;
  }

  linkedBookingDetails() {
    return this.bookingDetails;
  }

  linkedBookingEvent() {
    if (this.linkedBookingDetails()) {
      return this.eventDetails || this.linkedBookingDetails().event;
    }
    return false;
  }

  linkedBookingComment() {
    return this.linkedBookingDetails()?.comment || false;
  }

  linkedBookingRole() {
    return this.linkedBookingDetails()?.role;
  }

  comparePlannedTimes(entry) {
    let startDateAndTime1 = this.getPlannedStart();
    let startDateAndTime2 = entry.getPlannedStart();
    if (this.isShift() && this.shouldShowEnd()) startDateAndTime1 = this.getPlannedEnd();
    if (entry.isShift() && entry.shouldShowEnd())
      startDateAndTime2 = entry.getPlannedEnd();
    if (startDateAndTime1.isBefore(startDateAndTime2)) return -1;
    if (startDateAndTime1.isAfter(startDateAndTime2)) return 1;
    /* times are the same, show shifts first, put the ending of a shift before the start of the other shift */
    if (this.isShift()) {
      if (entry.isShift() && entry.shouldShowEnd()) return 1;
      return -1;
    }
    return 0;
  }

  serialize() {
    return {
      id: this.entryJSON.id,
      timesheetstart: serializeYesplanAPIDateAndTime(this.getTimesheetStart()),
      timesheetend: serializeYesplanAPIDateAndTime(this.getTimesheetEnd())
    };
  }
}
